import { useEffect, useState } from "react";
import { Link, Redirect } from "wouter";
import { useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMe,
  useGetMyOnboarding,
  useGetMyMsa,
  useSignMyMsa,
  getGetMyMsaQueryKey,
} from "@workspace/api-client-react";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  FileText,
  GraduationCap,
  Loader2,
  LogOut,
  Mail,
  PenLine,
  Phone,
  ShieldCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/onboarding/SignaturePad";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MsaContractDocument } from "@/components/onboarding/MsaContractDocument";
import { useToast } from "@/hooks/use-toast";
import {
  ONBOARDING_DOCUMENT_KEYS,
  DOCUMENT_LABELS,
  DOCUMENT_PATH_FIELDS,
  rejectionReasonLabel,
} from "@/lib/ficaReview";

// Surface a server-provided error message when available.
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const message =
        (data as { message?: unknown }).message ??
        (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

function formatSignDate(epochMs: number | null | undefined): string {
  if (!epochMs) return "";
  return new Date(epochMs).toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const AGENT_TYPE_LABELS: Record<string, string> = {
  field_sales: "Independent Field Sales Agent",
  independent_technician: "Independent Technician Referral Agent",
};

// Agent support contact. Mirrors the server defaults in api-server email.ts
// (AGENT_SUPPORT_EMAIL / AGENT_SUPPORT_PHONE) so the rejected-onboarding recovery
// path matches the support contact shown in transactional emails.
const SUPPORT_EMAIL = "agentsupport@mooya.co.za";
const SUPPORT_PHONE = "012 004 0043";

/**
 * Read-only "awaiting approval" surface shown to an agent after they submit
 * their onboarding application (and while it is rejected). No editing, no leads,
 * no balance — only their submitted details and the current decision.
 */
export function OnboardingStatus() {
  const { signOut } = useClerk();
  const { data: me } = useGetMe();
  const { data: onboarding, isLoading } = useGetMyOnboarding();

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // Established/approved agents belong in the app; editable applications belong
  // in the wizard. This page is only for submitted / rejected applications.
  if (me?.agentId != null || onboarding?.status === "approved") {
    return <Redirect to="/home" />;
  }
  if (
    onboarding?.status === "in_progress" ||
    onboarding?.status === "changes_requested"
  ) {
    return <Redirect to="/onboarding" />;
  }

  const rejected = onboarding?.status === "rejected";
  const submittedDate = onboarding?.submittedAt
    ? new Date(onboarding.submittedAt).toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  // Pre-filled appeal email so a rejected applicant has a one-tap recovery path
  // instead of a dead-end. We include their name and ID so support can locate
  // the FICA application immediately (the applicant's own details).
  const appealBody = [
    "Hi Mooya team,",
    "",
    "My onboarding application was not approved and I'd like to appeal / understand the next steps.",
    "",
    `Name: ${onboarding?.fullName ?? me?.fullName ?? ""}`,
    `ID number: ${onboarding?.idNumber ?? ""}`,
    "",
    "Thank you.",
  ].join("\n");
  const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    "Onboarding application appeal",
  )}&body=${encodeURIComponent(appealBody)}`;
  const supportTel = `tel:${SUPPORT_PHONE.replace(/[^\d+]/g, "")}`;

  return (
    <div className="font-sans min-h-[100dvh] w-full max-w-[480px] mx-auto bg-gray-50 flex flex-col text-[#0B132B]">
      <header className="bg-[#0B132B] text-white px-5 pt-6 pb-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <img
              src={`${import.meta.env.BASE_URL}mooya-mark.png`}
              alt="Mooya"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-lg tracking-tight">Mooya</span>
          </div>
          <button
            onClick={() => signOut()}
            className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <h1 className="text-xl font-bold mb-1">Application status</h1>
        <p className="text-sm text-slate-300">
          {me?.fullName ? `Hi ${me.fullName.split(" ")[0]}, ` : ""}
          here&apos;s where your onboarding stands.
        </p>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6 space-y-5">
        {rejected ? (
          <>
            <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-900">
                  Application not approved
                </p>
                {onboarding?.reviewerNotes ? (
                  <p className="text-sm text-red-800 mt-1 whitespace-pre-line">
                    {onboarding.reviewerNotes}
                  </p>
                ) : (
                  <p className="text-sm text-red-800 mt-1">
                    Review the document notes below, then reach out and
                    we&apos;ll help you get back on track.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-4">
              <p className="text-sm font-semibold text-[#0B132B]">
                Need to appeal or have questions?
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Our support team can review your application and help you
                resolve any issues with your onboarding.
              </p>
              <a
                href={supportMailto}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-[#E6007E] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#c4006b] transition-colors"
              >
                <Mail className="w-4 h-4" /> Contact support
              </a>
              <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Or call{" "}
                <a
                  href={supportTel}
                  className="font-medium text-[#0B132B] hover:underline"
                >
                  {SUPPORT_PHONE}
                </a>
              </p>
            </div>
          </>
        ) : (
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Awaiting account approval
              </p>
              <p className="text-sm text-amber-800 mt-1">
                Your application
                {submittedDate ? ` (submitted ${submittedDate})` : ""} is with
                your account manager. You&apos;ll get full access once it is
                approved.
              </p>
            </div>
          </div>
        )}

        {!rejected && <MsaSigningCard />}

        <Link
          href="/training"
          className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 hover:border-[#E6007E]/40 transition-colors"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#E6007E]/10 shrink-0">
            <GraduationCap className="w-5 h-5 text-[#E6007E]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0B132B]">
              Start your training
            </p>
            <p className="text-sm text-gray-600">
              Complete the product training and exam while you wait for
              approval.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400 ml-auto shrink-0" />
        </Link>

        <div className="flex items-start gap-3 rounded-xl bg-[#00A859]/10 p-4">
          <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
          <p className="text-sm text-[#0B132B]">
            Your submitted details are shown below for your records. They are
            locked while under review.
          </p>
        </div>

        <ReviewGroup title="Your details">
          <ReviewRow label="Full name" value={onboarding?.fullName ?? ""} />
          <ReviewRow label="ID number" value={onboarding?.idNumber ?? ""} />
          <ReviewRow label="Mobile" value={onboarding?.phone ?? ""} />
          <ReviewRow
            label="Address"
            value={[
              onboarding?.addressLine,
              onboarding?.city,
              onboarding?.province,
              onboarding?.postalCode,
            ]
              .filter(Boolean)
              .join(", ")}
          />
          <ReviewRow
            label="Operating area"
            value={onboarding?.preferredOperatingArea ?? ""}
          />
          <ReviewRow
            label="Agent type"
            value={
              onboarding?.agentType
                ? (AGENT_TYPE_LABELS[onboarding.agentType] ??
                  onboarding.agentType)
                : ""
            }
          />
        </ReviewGroup>

        <ReviewGroup title="Banking">
          <ReviewRow label="Bank" value={onboarding?.bankName ?? ""} />
          <ReviewRow label="Account" value={onboarding?.accountNumber ?? ""} />
          <ReviewRow
            label="Account type"
            value={onboarding?.accountType ?? ""}
          />
          <ReviewRow label="Branch code" value={onboarding?.branchCode ?? ""} />
        </ReviewGroup>

        <ReviewGroup title="Documents">
          {ONBOARDING_DOCUMENT_KEYS.map((key) => {
            const uploaded = !!onboarding?.[DOCUMENT_PATH_FIELDS[key]];
            const dr = (onboarding?.documentReviews ?? []).find(
              (d) => d.documentKey === key,
            );
            return (
              <DocumentReviewRow
                key={key}
                label={DOCUMENT_LABELS[key]}
                uploaded={uploaded}
                status={dr?.status ?? null}
                rejectionReason={dr?.rejectionReason ?? null}
                comment={dr?.comment ?? null}
              />
            );
          })}
        </ReviewGroup>
      </main>
    </div>
  );
}

function ReviewGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </p>
      </div>
      <div className="divide-y divide-gray-50">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-2.5 flex items-start justify-between gap-4">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-[#0B132B] text-right">
        {value || "—"}
      </span>
    </div>
  );
}

// A document row that also surfaces the reviewer's per-document decision. For a
// rejected document it shows the controlled reason plus any comment, so the
// agent knows exactly what to fix before resubmitting.
function DocumentReviewRow({
  label,
  uploaded,
  status,
  rejectionReason,
  comment,
}: {
  label: string;
  uploaded: boolean;
  status: "pending" | "accepted" | "rejected" | null;
  rejectionReason: string | null;
  comment: string | null;
}) {
  const statusLabel = !uploaded
    ? "—"
    : status === "accepted"
      ? "Accepted"
      : status === "rejected"
        ? "Rejected"
        : "Uploaded";
  const statusClass =
    status === "accepted"
      ? "text-[#00A859]"
      : status === "rejected"
        ? "text-red-700"
        : uploaded
          ? "text-[#0B132B]"
          : "text-gray-400";
  return (
    <div className="px-4 py-2.5">
      <div className="flex items-start justify-between gap-4">
        <span className="text-sm text-gray-500 shrink-0">{label}</span>
        <span className={`text-sm font-medium text-right ${statusClass}`}>
          {statusLabel}
        </span>
      </div>
      {status === "rejected" ? (
        <div className="mt-1.5 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
          <p className="text-xs font-medium text-red-800">
            {rejectionReasonLabel(rejectionReason)}
          </p>
          {comment ? (
            <p className="text-xs text-red-700 mt-0.5 whitespace-pre-line">
              {comment}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Master Service Agreement e-signature surface for field-sales agents. The MSA
// is a hard gate before activation: the agent must digitally sign their
// prefilled Field Sales Agent Agreement, after which Mooya counter-signs. This
// card adapts to the three pre-activation states (needs signature / awaiting
// countersignature / fully signed) and is only rendered when the server marks
// an MSA as required for this onboarding.
function MsaSigningCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: msa } = useGetMyMsa();
  const sign = useSignMyMsa();
  const [open, setOpen] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (open) {
      setSignatureImage(null);
      setConsent(false);
    }
  }, [open]);

  if (!msa?.required) return null;

  if (msa.fullySigned) {
    return (
      <div className="flex items-start gap-3 rounded-xl bg-[#00A859]/10 p-4">
        <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#0B132B]">
            Agreement fully signed
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Your Field Sales Agent Agreement is signed by you and counter-signed
            by Mooya.
          </p>
          {msa.documentAvailable ? (
            <a
              href="/api/me/msa/document"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#E6007E] hover:underline"
            >
              <FileText className="w-4 h-4" /> View signed agreement
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  if (msa.agentSigned) {
    const signedOn = formatSignDate(msa.agentSignedAt);
    return (
      <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
        <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            Agreement awaiting Mooya counter-signature
          </p>
          <p className="text-sm text-amber-800 mt-1">
            You signed your Field Sales Agent Agreement
            {signedOn ? ` on ${signedOn}` : ""}. Mooya will counter-sign before
            your account goes live.
          </p>
          {msa.documentAvailable ? (
            <a
              href="/api/me/msa/document"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#0B132B] hover:underline"
            >
              <FileText className="w-4 h-4" /> View your signed copy
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  const registeredName = msa.prefill.fullName?.trim() ?? "";
  const canSubmit = !!signatureImage && consent && !sign.isPending;

  const submit = async () => {
    if (!canSubmit || !signatureImage) return;
    try {
      await sign.mutateAsync({
        data: { signatureImage, consent },
      });
      await queryClient.invalidateQueries({ queryKey: getGetMyMsaQueryKey() });
      toast({
        title: "Agreement signed",
        description: "Thank you. Mooya will counter-sign your agreement.",
      });
      setOpen(false);
    } catch (err) {
      toast({
        title: "Could not sign agreement",
        description: apiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="rounded-xl border border-[#E6007E]/30 bg-[#E6007E]/5 p-4">
        <div className="flex items-start gap-3">
          <PenLine className="w-5 h-5 text-[#E6007E] mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0B132B]">
              Sign your Field Sales Agent Agreement
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Before your account can go live, please review and digitally sign
              your Master Service Agreement.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#E6007E] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#c4006b] transition-colors"
            >
              <PenLine className="w-4 h-4" /> Review &amp; sign
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Field Sales Agent Agreement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please read your Field Sales Agent Agreement in full, then sign
              below.
            </p>

            <div className="max-h-[55vh] overflow-y-auto">
              <MsaContractDocument
                contract={msa.contract}
                prefill={msa.prefill}
                agentSignedName={msa.agentSignedName}
                agentSignedAt={msa.agentSignedAt}
                agentSignatureImage={msa.agentSignatureImage}
                adminSignedName={msa.adminSignedName}
                adminSignedAt={msa.adminSignedAt}
                adminSignatureImage={msa.adminSignatureImage}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Draw your signature to sign</Label>
              <p className="text-xs text-gray-500">
                Signing as{" "}
                <span className="font-semibold text-[#0B132B]">
                  {registeredName || "your registered name"}
                </span>{" "}
                — the name on your application.
              </p>
              <SignaturePad
                ariaLabel="Draw your signature"
                onChange={setSignatureImage}
              />
            </div>

            <div className="flex items-start gap-2.5">
              <Checkbox
                id="msa-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="msa-consent"
                className="text-sm font-normal leading-snug text-gray-600 cursor-pointer"
              >
                I have read and agree to the Field Sales Agent Agreement, and I
                consent to signing it electronically.
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={sign.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
            >
              {sign.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Sign agreement"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
